import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import energyTypeIcon from "../../assets/energy_type.png";
import "./KeyTariffFactsModal.css";

const KeyTariffFactsModal = ({ isOpen, onClose, quote, formData }) => {
  const navigate = useNavigate();
  const singleRateBilling = formData?.singleRateBilling || false;

  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !quote) {
    return null;
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAmendQuote = () => {
    sessionStorage.setItem("navigated", "true");
    navigate("/", { state: { formData } });
  };

  const renderMeterTypeRates = (quoteData, isSingleRate) => {
    if (quoteData.meterType === "Single" || isSingleRate) {
      return (
        <>
          <p className="ktf-label">
            {quoteData.fuelType === "Electricity" ? "Electricity" : "Gas"} Unit
            cost:
          </p>
          <p className="ktf-value">{quoteData.pricePerKwh1}</p>
        </>
      );
    }
    if (quoteData.meterType === "Day/Night") {
      return (
        <>
          <p className="ktf-label">Day rate:</p>
          <p className="ktf-value">{quoteData.pricePerKwh1}</p>
          <p className="ktf-label">Night rate:</p>
          <p className="ktf-value">{quoteData.pricePerKwh2}</p>
        </>
      );
    }
    if (quoteData.meterType === "Peak/Off-Peak") {
      return (
        <>
          <p className="ktf-label">Peak rate:</p>
          <p className="ktf-value">{quoteData.pricePerKwh1}</p>
          <p className="ktf-label">Off-peak rate:</p>
          <p className="ktf-value">{quoteData.pricePerKwh2}</p>
        </>
      );
    }
    if (quoteData.meterType === "RHT") {
      return (
        <>
          <p className="ktf-label">Price1:</p>
          <p className="ktf-value">{quoteData.pricePerKwh1}</p>
          <p className="ktf-label">Price2:</p>
          <p className="ktf-value">{quoteData.pricePerKwh2}</p>
        </>
      );
    }
    return (
      <>
        <p className="ktf-label">
          {quoteData.energyType === "Electricity" ? "Electricity " : "Gas "}
          Unit cost:
        </p>
        <p className="ktf-value">{quoteData.unitRate}</p>
      </>
    );
  };

  return (
    <div
      className="ktf-modal-backdrop"
      onClick={handleBackdropClick}
      aria-hidden="true"
    >
      <div className="ktf-modal-wrapper">
        <dialog
          open
          className="ktf-modal-content"
          aria-labelledby="ktf-modal-title"
        >
          {/* Header */}
          <div className="ktf-modal-header">
            <button
              type="button"
              onClick={onClose}
              className="ktf-modal-close-btn"
              aria-label="Close key tariff facts modal"
            >
              ×
            </button>

            <h3 id="ktf-modal-title" className="ktf-modal-title">
              {quote.months} Months {quote.type}
            </h3>
          </div>

          {/* Content */}
          <div className="ktf-modal-body">
            {/* Fuel Type */}
            <div className="ktf-fuel-type-section">
              <ul className="ktf-fuel-type-list">
                <li className="ktf-fuel-type-item">
                  <span
                    className={`ktf-fuel-type-icon ${quote.fuelType.toLowerCase()}`}
                    style={{ backgroundImage: `url(${energyTypeIcon})` }}
                    aria-hidden="true"
                  />
                  <span className="sr-only">{quote.fuelType} energy type</span>
                  {quote.fuelType}
                </li>
              </ul>
            </div>

            {/* Key Tariff Facts */}
            <div className="ktf-tariff-facts-section">
              <h4 className="ktf-section-title">Key tariff facts</h4>

              <p className="ktf-label">Total estimated annual cost</p>
              <p className="ktf-value">
                <span>£{quote.estimatedTotalCost}*</span>
                <small className="ktf-value-small"> (ex VAT)</small>
              </p>

              <p className="ktf-label">
                {quote.fuelType === "Electricity" ? "Electricity" : "Gas"}{" "}
                annual usage
              </p>
              <p className="ktf-value">
                {" "}
                {quote?.meterType === "Day/Night" ||
                quote?.meterType === "Peak/Off-Peak" ? (
                  <span>
                    {(
                      quote?.annualConsumption + quote?.annualConsumptionNight
                    ).toLocaleString()}{" "}
                    kWh
                  </span>
                ) : (
                  <span>{quote?.annualConsumption.toLocaleString()} kWh</span>
                )}
              </p>

              {renderMeterTypeRates(quote, singleRateBilling)}

              <p className="ktf-label">
                {quote.fuelType === "Electricity" ? "Electricity" : "Gas"}{" "}
                standing charge
              </p>
              <p className="ktf-value">{quote.standingCharge} per day</p>
            </div>

            {/* Action Buttons and Links */}
            <div className="ktf-actions-section">
              <ul className="ktf-actions-list">
                <li className="ktf-action-item">
                  <button
                    type="button"
                    onClick={onClose}
                    className="ktf-primary-btn"
                    aria-label="Close modal and return to quotes"
                  >
                    Back to Quotes
                  </button>
                </li>

                <li className="ktf-action-item extra-padding">
                  <button
                    type="button"
                    onClick={handleAmendQuote}
                    className="ktf-link"
                    aria-label="Amend this quote"
                  >
                    Amend this quote
                  </button>
                  <span className="ktf-link-arrow">›</span>
                </li>

                <li className="ktf-action-item no-padding">
                  <a
                    href="/supply-contract-terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ktf-blue-link"
                    aria-label="View Supply Contract Terms and Conditions (opens in new tab)"
                  >
                    Supply Contract Terms and Conditions
                  </a>
                </li>

                <li className="ktf-action-item no-padding">
                  <a
                    href="/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ktf-blue-link"
                    aria-label="View Terms of use (opens in new tab)"
                  >
                    Terms of use
                  </a>
                </li>
              </ul>
            </div>

            {/* Disclaimer */}
            <p className="ktf-disclaimer">
              <small className="ktf-disclaimer-text">
                *This quote is estimated based on the consumption information
                you provided. If your actual consumption is different to the
                estimate provided, the final price may change. If your supply
                contract is terminated early, you may be liable to ENGIE for
                charges and costs as set out in the Supply Contract Terms and
                Conditions.
              </small>
            </p>

            {/* Transfer Section */}
            <div className="ktf-transfer-section">
              <p className="ktf-transfer-paragraph">
                <strong className="ktf-bold-text">
                  Electricity exclusions:
                </strong>
              </p>
              <p className="ktf-transfer-paragraph">
                All prices exclude VAT at a rate of 20% and Climate Change Levy
                (CCL). If these apply to you, we'll add them at the current
                rates.
              </p>
              <p className="ktf-transfer-paragraph">
                <strong className="ktf-bold-text">Gas exclusions:</strong>
              </p>
              <p className="ktf-transfer-paragraph">
                All charges are exclusive of VAT and Climate Change Levy. If
                these apply to you, we'll add them at the current rates.
              </p>
              <p className="ktf-transfer-paragraph">
                <strong className="ktf-bold-text">Green Gas supply:</strong>
              </p>
              <p className="ktf-transfer-paragraph">
                We source our Green Gas from a variety of different natural
                sources as agreed under the definition for Green Gas. For every
                kWh consumed we purchase a Green Gas Certificate from the Green
                Gas Certificate Scheme(GGCS). We will provide confirmation that
                your business has purchased Green Gas.
              </p>
              <p className="ktf-transfer-paragraph no-margin">Please note:</p>
              <p className="ktf-transfer-paragraph no-margin">
                Guard Green Gas is available on contracts up to 2 year duration.
                The Guard Green Gas contract must end before 30/6/22.
              </p>
              <p className="ktf-transfer-paragraph">
                <strong className="ktf-bold-text">Transfer of supply:</strong>
              </p>
              <p className="ktf-transfer-paragraph">
                Gas or electricity supply to your site shall not commence until
                the later of (i) registration of your meter, or (ii) 21 days
                after the date on which the supply contract becomes binding on
                the parties.
              </p>
            </div>
          </div>
        </dialog>
      </div>
    </div>
  );
};

KeyTariffFactsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  quote: PropTypes.shape({
    months: PropTypes.number,
    type: PropTypes.string,
    fuelType: PropTypes.string,
    estimatedTotalCost: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    meterType: PropTypes.string,
    annualConsumption: PropTypes.number,
    annualConsumptionNight: PropTypes.number,
    pricePerKwh1: PropTypes.string,
    pricePerKwh2: PropTypes.string,
    energyType: PropTypes.string,
    unitRate: PropTypes.string,
    standingCharge: PropTypes.string,
  }),
  formData: PropTypes.shape({
    singleRateBilling: PropTypes.bool,
  }),
};

export default KeyTariffFactsModal;
